<script lang='ts'>

    /* IMPORTS --------------------------------------------------------------------------------- */

    // CASSIOPEIA
    import {
        addMessage,
        SubscriptionForm,
        SESSION_USER,
        ProgressLines,
        Form,
        Button,
        FieldsetProducts,
        FieldsetPrice,
        FieldsetVolume,
        FieldsetOtherSpecs,
    } from '@smatch/cassiopeia-web-components';

    // COMPONENTS

    // TYPES
    import type { Subscription, FormComponent } from 'src/app/types';
    import type { Writable } from 'svelte/store';

    // UTILITES
    import { navigate } from 'svelte-navigator';

    /* TYPES ----------------------------------------------------------------------------------- */

    type Step = '1' | '2' | '3' | '4';

    /* PROPS ----------------------------------------------------------------------------------- */

    export let subscription: Subscription | undefined = undefined;

    export let action: 'create' | 'update';

    /* INITIALIZE ------------------------------------------------------------------------------ */

    let SearchTaskForm: FormComponent | null = null;

    $: SUBSCRIPTION = SearchTaskForm === null ? undefined : SearchTaskForm.DATA as Writable<Subscription>;

    const STEPS = {
        1: 'Step 1: Products',
        2: 'Step 2: Pricing',
        3: 'Step 3: Volume',
        4: 'Step 4: Other Specifications',
    };

    let currentStep: Step = '1';

    /* INTERNALS ------------------------------------------------------------------------------- */

    const beforeSave = (): void => {
        if ($SESSION_USER === null || $SUBSCRIPTION === undefined) {
            return;
        }

        if ($SUBSCRIPTION.ownerKey === '') {
            $SUBSCRIPTION.ownerKey = $SESSION_USER._.key;
        }
    };

    const onSave = (): void => {
        addMessage({
            heading: `component.subscription.form.${ action }.success.heading`,
            type: 'success',
        });
        navigate('/subscriptions');
    };

    const goToStep = (step: Step): void => {
        currentStep = step;
    };

</script>

<SubscriptionForm { action } { subscription } { beforeSave } on:save = { onSave } bind:this = { SearchTaskForm }>

    {#if $SUBSCRIPTION !== undefined}

        <div class = 's8-form-container'>

            <div class = 's8-banner'>
                <div class = 's8-banner-text'>
                    Share your search and get + 1% discount on your matched deal!
                </div>
                <div class = 's8-banner-triangle'></div>
            </div>

            <ProgressLines steps = { STEPS } current = { currentStep } />

            {#if currentStep === '1'}

                <Form onSubmit = { goToStep.bind(null, '2') } isSubmittedOnEnter = { false }>
                    <FieldsetProducts bind:subscription = { $SUBSCRIPTION } />
                    <div class = 's8-single-button'>
                        <Button label = 'Next' type = 'submit' />
                    </div>
                </Form>

            {:else if currentStep === '2'}

                <Form onSubmit = { goToStep.bind(null, '3') } isSubmittedOnEnter = { false }>
                    <FieldsetPrice bind:subscription = { $SUBSCRIPTION } />
                    <div class = 's8-button-group'>
                        <Button label = 'Back' style = 'outline' on:click = { goToStep.bind(null, '1') } />
                        <Button label = 'Next' type = 'submit' />
                    </div>
                </Form>

            {:else if currentStep === '3'}

                <Form onSubmit = { goToStep.bind(null, '4') } isSubmittedOnEnter = { false }>
                    <FieldsetVolume bind:subscription = { $SUBSCRIPTION } />
                    <div class = 's8-button-group'>
                        <Button label = 'Back' style = 'outline' on:click = { goToStep.bind(null, '2') } />
                        <Button label = 'Next' type = 'submit' />
                    </div>
                </Form>

            {:else}

                <FieldsetOtherSpecs bind:subscription = { $SUBSCRIPTION } />
                <div class = 's8-button-group'>
                    <Button label = 'Back' style = 'outline' on:click = { goToStep.bind(null, '3') } />
                    <Button
                        label = { `Finish & ${ action === 'create' ? 'Create' : 'Update' }` }
                        type = 'submit'
                    />
                </div>

            {/if}

        </div>

    {/if}

</SubscriptionForm>

<style lang='postcss'>
    div.s8-form-container {
        :global(ul.s8-progress-lines) {
            margin-bottom: 15px;
            margin-top: 45px;
        }
    }

    div.s8-banner {
        position: relative;
        width: 100%;
    }

    div.s8-banner-text {
        background-color: rgba(68, 131, 95, 1);
        color: white;
        width: fit-content;
        padding: 10px;
        position: absolute;
        right: -34px;
        top: -55px;
        z-index: 10;
        white-space: no-wrap;
    }

    div.s8-banner-triangle {
        width: 11px;
        height: 15px;
        background: linear-gradient(135deg, #666 50%, transparent 50%);
        position: absolute;
        right: -34px;
        top: -17px;
    }

    div.s8-button-group {
        display: flex;
        justify-content: space-between;
    }

    div.s8-single-button {
        justify-self: self-end;
    }
</style>